import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { P, Text } from '@mxenabled/mxui'

import { StatusBar, useGlobalCopyStore, useRecurringTransactionsStore } from '../../common'
import { useDimensions } from '../../common/hooks/useDimensions'

import { RecurrenceLegend } from './micro/RecurrenceLegend'
import { OverlappingMerchantLogos } from './micro/OverlappingMerchantLogos'
import { MerchantLogosRow } from './micro/MerchantLogosRow'
import { formatCurrency } from '../../core/utils/NumberFormatting'

export const MicroWidgetContent = observer(() => {
  const theme = useTheme()
  const [containerRef, { width }] = useDimensions()
  const {
    upcomingRecurrences,
    expenseTotal,
    missedExpenseTotal,
    paidExpenseTotal,
    upcomingExpenseTotal,
  } = useRecurringTransactionsStore()
  const { recurring: copy } = useGlobalCopyStore()

  const isSmallScreen = width <= 288
  const isLargeScreen = width >= 450
  const logoSize = 32
  const hasLogos = upcomingRecurrences.length > 0

  const expectedExpenseTotalFormatted = React.useMemo(() => {
    return formatCurrency(expenseTotal, expenseTotal === 0 ? '0,0' : '0,0.00')
  }, [expenseTotal])

  const data = React.useMemo(() => {
    const recurrence = [
      { color: theme.palette.success.main, percentage: `${paidExpenseTotal / expenseTotal}` },
    ]
    if (missedExpenseTotal) {
      recurrence.push({
        color: theme.palette.error.main,
        percentage: `${missedExpenseTotal / expenseTotal}`,
      })
    }
    return recurrence
  }, [
    paidExpenseTotal,
    expenseTotal,
    missedExpenseTotal,
    theme.palette.success.main,
    theme.palette.error.main,
  ])

  const renderLogosSection = () => {
    if (!hasLogos) {
      return <Stack sx={{ width: isSmallScreen ? 64 : 104 }} />
    }

    return isSmallScreen ? (
      <OverlappingMerchantLogos
        logoSize={logoSize}
        maxLogos={3}
        upcomingRecurrences={upcomingRecurrences.slice(0, 3)}
      />
    ) : (
      <MerchantLogosRow maxLogos={3} upcomingRecurrences={upcomingRecurrences.slice(0, 3)} />
    )
  }

  const recurrenceLegend = [
    {
      amount: paidExpenseTotal,
      bgColor: 'success.main',
      label: copy.paid,
    },
    {
      amount: missedExpenseTotal,
      bgColor: 'error.main',
      label: copy.missed,
    },
    {
      amount: upcomingExpenseTotal,
      bgColor: 'neutral.light',
      label: copy.upcoming,
    },
  ]

  return (
    <>
      <Stack ref={containerRef} sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Stack
          sx={[
            expectedExpenseTotalFormatted.length >= 10 && isSmallScreen
              ? { flexDirection: 'column' }
              : { flexDirection: 'row', gap: 2 },
            {
              alignItems: 'baseline',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          ]}
        >
          <Text
            sx={{ width: '100%' }}
            variant={expectedExpenseTotalFormatted.length >= 10 && isSmallScreen ? 'h3' : 'h2'}
          >
            {expectedExpenseTotalFormatted}
          </Text>

          <P sx={{ pr: 12 }} variant="caption">
            {copy.expected}
          </P>
        </Stack>
        {renderLogosSection()}
      </Stack>

      <Stack sx={{ pt: 12 }}>
        <StatusBar data={data} />

        <Stack
          sx={{
            alignItems: 'center',
            flexDirection: 'row',
            gap: 16,
            mt: 4,
          }}
        >
          {recurrenceLegend.map((legend, index) => (
            <RecurrenceLegend
              amount={legend.amount}
              bgcolor={legend.bgColor}
              isLargeScreen={isLargeScreen}
              isSmallScreen={isSmallScreen}
              key={`recurrence-legend-${index}`}
              label={legend.label}
            />
          ))}
        </Stack>
      </Stack>
    </>
  )
})
